import React, { useState } from 'react';
import Rating from '../../../../components/Rating';
import styled from '@emotion/styled';
import { EmotionBreakpoint } from 'dw-us-ui';

const ModalOverlay = styled.div`
	display: ${(props) => (props.show ? 'flex' : 'none')};
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: 1000;
	align-items: center;
	justify-content: center;
	padding: 20px;
`;

const ModalContainer = styled.div`
	background: #ffffff;
	border-radius: 8px;
	max-width: 568px;
	width: 100%;
	position: relative;
	padding: 36px;
	max-height: 90vh;
	overflow-y: auto;

	${EmotionBreakpoint('mobile')} {
		padding: 24px;
		max-height: 95vh;
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: 12px;
	right: 12px;
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		opacity: 0.7;
	}
`;

const CloseIcon = styled.svg`
	width: 16px;
	height: 16px;
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	align-items: center;
	justify-content: center;
`;

const ModalProductSection = styled.div`
	display: flex;
	gap: 24px;
	align-items: flex-start;
	width: 100%;

	${EmotionBreakpoint('mobile')} {
		flex-direction: column;
		align-items: center;
	}
`;

const ModalProductImage = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
	flex-shrink: 0;
	width: 92px;

	${EmotionBreakpoint('mobile')} {
		width: 120px;
	}
`;

const ModalBottleImage = styled.img`
	width: 70px;
	height: auto;
	max-height: 271px;
	object-fit: contain;

	${EmotionBreakpoint('mobile')} {
		width: 90px;
	}
`;

const ModalProductInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-width: 0;
`;

const ModalProductName = styled.h3`
	font-family: 'Roboto', sans-serif;
	font-weight: 700;
	font-size: 20px;
	line-height: 1.25;
	color: #222222;
	margin: 0;
	text-align: left;

	.fox & {
		font-family: 'Roboto', sans-serif;
	}
	.wsj & {
		font-family: Arial, sans-serif;
	}
	.npr & {
		font-family: 'Gotham', sans-serif;
	}
	.osw & {
		font-family: 'Neue Aachen Pro', sans-serif;
	}
`;

const RatingSection = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const StarsContainer = styled.div`
	display: flex;
	gap: 4px;
	align-items: center;
`;

const StarIcon = styled.svg`
	width: 16px;
	height: 16px;
	fill: #fbbf24;
`;

const RatingValue = styled.span`
	font-family: 'Roboto', sans-serif;
	font-weight: 600;
	font-size: 14px;
	line-height: 24px;
	color: #222222;
	.wsj & {
		font-family: Arial, sans-serif;
	}
`;

const ProductDetails = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	font-size: 16px;
	line-height: 1.35;
	color: #222222;
	text-align: left;
`;

const ProductDetailRow = styled.p`
	margin: 0;

	strong {
		font-weight: 700;
	}

	span {
		font-weight: 400;
	}
`;

const ProductDescription = styled.p`
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 16px;
	line-height: 1.35;
	color: #222222;
	margin: 0;
	text-align: left;
	${EmotionBreakpoint('mobile')} {
		max-height: ${(props) => (props.showDescription ? '500px' : '0')};
		opacity: ${(props) => (props.showDescription ? '1' : '0')};
		overflow: hidden;
		transition: max-height 0.4s ease, opacity 0.3s ease;
	}

	.wsj & {
		font-family: Arial, sans-serif;
	}
`;

const ReadMore = styled.div`
	cursor: pointer;
	align-items: center;
	gap: 4px;
	font-weight: 500;
	font-size: 14px;
	line-height: 24px;
	color: #222222;
	display: none;

	${EmotionBreakpoint('mobile')} {
		display: inline-flex;
	}
`;

const CarouselSection = styled.div`
	display: flex;
	gap: 34px;
	align-items: center;
	justify-content: center;
	width: 100%;
`;

const CarouselButton = styled.button`
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		opacity: 0.7;
	}

	&:disabled {
		opacity: 0.3;
		cursor: not-allowed;
	}
`;

const CarouselArrow = styled.svg`
	width: 9px;
	height: 15px;

	&.right {
		transform: rotate(180deg);
	}
`;

const CarouselDots = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const CarouselDot = styled.button`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	border: none;
	background: ${(props) => (props.active ? '#B1927B' : '#E5E5E5')};
	cursor: pointer;
	padding: 0;
	transition: background 0.3s ease;

	&:hover {
		background: ${(props) => (props.active ? '#B1927B' : '#BCBCBC')};
	}

	.law & {
		background: ${(props) => (props.active ? '#B1927B' : '#E5E5E5')};
	}
	.fox & {
		background: ${(props) => (props.active ? '#222' : '#E5E5E5')};
	}
	.wsj & {
		background: ${(props) => (props.active ? '#2B2A2F' : '#E5E5E5')};
	}
	.npr & {
		background: ${(props) => (props.active ? '#2B2A2F' : '#E5E5E5')};
	}
	.osw & {
		background: ${(props) => (props.active ? '#2B2A2F' : '#E5E5E5')};
	}
`;

const ModalButtonsSection = styled.div`
	display: flex;
	gap: 24px;
	width: 100%;
`;

const ModalButton = styled.button`
	flex: 1;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px 24px;
	border-radius: 4px;
	font-family: 'Roboto', sans-serif;
	font-weight: 700;
	font-size: 15px;
	letter-spacing: 0.45px;
	text-transform: uppercase;
	cursor: pointer;
	transition: all 0.3s ease;

	&.cancel {
		background: transparent;
		border: 1px solid #222222;
		color: #222222;

		&:hover {
			background: #f5f5f5;
		}
	}

	&.primary {
		background: #117b53;
		border: none;
		color: #ffffff;

		&:hover {
			background: #0d5f40;
		}
	}

	.law &.primary {
		background: #117b53;
		&:hover {
			background: #0d5f40;
		}
	}
	.fox &.primary {
		background: #222;
		&:hover {
			background: #000;
		}
	}
	.wsj &.primary {
		font-family: Arial, sans-serif;
		background: #e92440;
		&:hover {
			background: #1a1a1d;
		}
	}
	.npr &.primary {
		background: #2b2a2f;
		&:hover {
			background: #1a1a1d;
		}
	}
	.osw &.primary {
		background: #2b2a2f;
		&:hover {
			background: #1a1a1d;
		}
	}
`;

const SwapModal = ({
	showSwapModal,
	closeSwapModal,
	activeSwaps,
	currentCarouselSlide,
	goToSlide,
	swapWines,
	previousSlide,
	nextSlide,
}) => {
	const [showDescription, setShowDescription] = useState(false);

	return (
		<ModalOverlay show={showSwapModal} onClick={closeSwapModal}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<CloseButton onClick={closeSwapModal}>
					<CloseIcon viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'>
						<path
							d='M15 1L1 15M1 1L15 15'
							stroke='#222222'
							strokeWidth='2'
							strokeLinecap='round'
						/>
					</CloseIcon>
				</CloseButton>

				<ModalContent>
					{activeSwaps.length > 0 && activeSwaps[currentCarouselSlide] && (
						<>
							<ModalProductSection>
								<ModalProductImage>
									<ModalBottleImage
										src={
											`/images/us/en/product/${activeSwaps[currentCarouselSlide]?.product?.itemCode}.png` ||
											'/images/us/common/default_bottle_small.png'
										}
										alt={
											activeSwaps[currentCarouselSlide]?.product?.name ||
											'Wine bottle'
										}
										onError={(e) => {
											e.currentTarget.onerror = null;
											e.currentTarget.src =
												'/images/us/common/default_bottle_small.png';
										}}
									/>
								</ModalProductImage>

								<ModalProductInfo>
									<ModalProductName>
										{activeSwaps[currentCarouselSlide]?.product?.name}{' '}
										{activeSwaps[currentCarouselSlide]?.product?.vintage}
									</ModalProductName>

									<RatingSection>
										<StarsContainer>
											<Rating
												ratingValue={
													activeSwaps[currentCarouselSlide]?.product
														?.ratingDetails?.productRating?.avgRating ?? 0
												}
											/>
										</StarsContainer>
										{activeSwaps[currentCarouselSlide]?.product?.ratingDetails
											?.productRating?.avgRating && (
											<RatingValue>
												{
													activeSwaps[currentCarouselSlide]?.product
														?.ratingDetails?.productRating?.avgRating
												}
											</RatingValue>
										)}
									</RatingSection>

									<ProductDetails>
										<ProductDetailRow>
											<strong>Style: </strong>
											<span>
												{activeSwaps[currentCarouselSlide]?.product
													?.styleName || 'N/A'}
											</span>
										</ProductDetailRow>
										<ProductDetailRow>
											<strong>Grape: </strong>
											<span>
												{activeSwaps[currentCarouselSlide]?.product
													?.grapeName || 'N/A'}
											</span>
										</ProductDetailRow>
										<ProductDetailRow>
											<strong>Country: </strong>
											<span>
												{activeSwaps[currentCarouselSlide]?.product
													?.countryName || 'N/A'}
											</span>
										</ProductDetailRow>
										<ProductDetailRow>
											<strong>Region: </strong>
											<span>
												{activeSwaps[currentCarouselSlide]?.product
													?.regionName || 'N/A'}
											</span>
										</ProductDetailRow>
									</ProductDetails>

									<ProductDescription
										showDescription={showDescription}
										dangerouslySetInnerHTML={{
											__html:
												activeSwaps[currentCarouselSlide]?.product
													?.description || '',
										}}
									/>

									<ReadMore
										onClick={() => setShowDescription(!showDescription)}
									>
										Read {showDescription ? 'Less' : 'More'}{' '}
										<svg
											width='12'
											height='8'
											viewBox='0 0 12 8'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
											style={{
												rotate: showDescription ? '180deg' : '0deg',
												transition: 'rotate 0.3s ease',
											}}
										>
											<path
												d='M6 7.4L0 1.4L1.4 0L6 4.6L10.6 0L12 1.4L6 7.4Z'
												fill='#1F1F1F'
											/>
										</svg>
									</ReadMore>
								</ModalProductInfo>
							</ModalProductSection>

							<CarouselSection>
								<CarouselButton
									onClick={previousSlide}
									disabled={currentCarouselSlide === 0}
								>
									<CarouselArrow
										viewBox='0 0 9 15'
										xmlns='http://www.w3.org/2000/svg'
									>
										<path
											d='M8 1L1.5 7.5L8 14'
											stroke='#B1927B'
											strokeWidth='2'
											strokeLinecap='round'
											fill='none'
										/>
									</CarouselArrow>
								</CarouselButton>

								<CarouselDots>
									{activeSwaps.map((_, index) => (
										<CarouselDot
											key={index}
											active={index === currentCarouselSlide}
											onClick={() => goToSlide(index)}
										/>
									))}
								</CarouselDots>

								<CarouselButton
									onClick={nextSlide}
									disabled={currentCarouselSlide === activeSwaps.length - 1}
								>
									<CarouselArrow
										className='right'
										viewBox='0 0 9 15'
										xmlns='http://www.w3.org/2000/svg'
									>
										<path
											d='M8 1L1.5 7.5L8 14'
											stroke='#B1927B'
											strokeWidth='2'
											strokeLinecap='round'
											fill='none'
										/>
									</CarouselArrow>
								</CarouselButton>
							</CarouselSection>

							<ModalButtonsSection>
								<ModalButton
									className='cancel'
									onClick={() => {
										setShowDescription(false);
										closeSwapModal();
									}}
								>
									Cancel
								</ModalButton>
								<ModalButton
									className='primary'
									onClick={() => {
										setShowDescription(false);
										swapWines();
									}}
								>
									Swap Wine
								</ModalButton>
							</ModalButtonsSection>
						</>
					)}
				</ModalContent>
			</ModalContainer>
		</ModalOverlay>
	);
};

export default SwapModal;
